import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header, Footer } from './Layout';
import { Card, CardContent } from './ui/card';
import { publicApi } from '../utils/api';
import {
  CheckCircle, XCircle, Loader2, Shield, User, Calendar, CreditCard, ArrowLeft
} from 'lucide-react';

const TIER_COLORS = {
  Starter: { accent: '#00E5FF', bg: 'bg-[#00E5FF]/10', text: 'text-[#00E5FF]' },
  Professional: { accent: '#7C4DFF', bg: 'bg-[#7C4DFF]/10', text: 'text-[#7C4DFF]' },
  Elite: { accent: '#FFD740', bg: 'bg-[#FFD740]/10', text: 'text-[#FFD740]' },
  Inactive: { accent: '#5C7599', bg: 'bg-white/10', text: 'text-[#5C7599]' },
  'N/A': { accent: '#5C7599', bg: 'bg-white/10', text: 'text-[#5C7599]' },
};

export function VerifyMembershipPage() {
  const { membershipId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verify = async () => {
      try {
        const { data: res } = await publicApi.get(`/api/verify/${membershipId}`);
        setData(res);
      } catch (e) {
        setData({ verified: false, message: 'Verification failed. Invalid membership ID.' });
      }
      setLoading(false);
    };
    verify();
  }, [membershipId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#00E5FF] mx-auto mb-4" />
          <p className="text-[#A0B4CC]">Verifying membership...</p>
        </div>
      </div>
    );
  }

  const isVerified = data?.verified;
  const member = data?.member || {};
  const tier = member.tier || 'N/A';
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS['N/A'];
  const isActive = member.subscription_status === 'active';
  const selfieUrl = member.selfie_url ? (member.selfie_url.startsWith('/') ? member.selfie_url : member.selfie_url) : '';
  const memberSince = member.member_since ? new Date(member.member_since).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#000B18]">
      <Header />
      <main className="pt-20 pb-12 px-4">
        <div className="max-w-md mx-auto">
          {/* Verification Result */}
          {isVerified ? (
            <div data-testid="verification-result">
              {/* Verified Badge */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-4 border-2 border-[#00E676]/30">
                  <CheckCircle className="w-10 h-10 text-[#00E676]" />
                </div>
                <h1 className="font-outfit text-2xl font-bold text-[#00E676]" data-testid="verified-badge">
                  Verified
                </h1>
                {isActive && (
                  <p className="text-lg font-semibold mt-1" style={{ color: tierStyle.accent }} data-testid="tier-badge">
                    {tier} Member
                  </p>
                )}
                {!isActive && (
                  <p className="text-[#FF1744] text-sm mt-1">Subscription Inactive</p>
                )}
              </div>

              {/* Member Card */}
              <Card className="bg-[#041A3B] border-[#00E676]/20 overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-6">
                    {/* Photo + Name */}
                    <div className="flex items-center gap-4 mb-6">
                      {selfieUrl ? (
                        <img
                          src={selfieUrl}
                          alt={member.name}
                          className="w-20 h-20 rounded-xl object-cover border-2 border-[#00E676]/30"
                          data-testid="member-photo"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-xl bg-white/10 flex items-center justify-center">
                          <User className="w-10 h-10 text-[#5C7599]" />
                        </div>
                      )}
                      <div>
                        <h2 className="font-outfit text-xl font-bold text-white" data-testid="member-name">
                          {member.name}
                        </h2>
                        <p className="text-[#A0B4CC] text-sm font-mono" data-testid="member-id">
                          {member.membership_id}
                        </p>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-[#5C7599]" />
                          <span className="text-[#A0B4CC] text-sm">Membership ID</span>
                        </div>
                        <span className="text-white text-sm font-mono font-medium">{member.membership_id}</span>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#5C7599]" />
                          <span className="text-[#A0B4CC] text-sm">Tier</span>
                        </div>
                        <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${tierStyle.bg} ${tierStyle.text}`}>
                          {tier}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#5C7599]" />
                          <span className="text-[#A0B4CC] text-sm">Status</span>
                        </div>
                        <span className={`text-sm font-medium ${isActive ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {memberSince && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#5C7599]" />
                            <span className="text-[#A0B4CC] text-sm">Member Since</span>
                          </div>
                          <span className="text-white text-sm">{memberSince}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-3 bg-[#00E676]/5 border-t border-[#00E676]/10 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    <span className="text-[#00E676] text-sm font-medium">All Global Access - Verified Member</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Not Verified */
            <div className="text-center" data-testid="verification-failed">
              <div className="w-20 h-20 rounded-full bg-[#FF1744]/10 flex items-center justify-center mx-auto mb-4 border-2 border-[#FF1744]/30">
                <XCircle className="w-10 h-10 text-[#FF1744]" />
              </div>
              <h1 className="font-outfit text-2xl font-bold text-[#FF1744] mb-2">
                Not Verified
              </h1>
              <p className="text-[#A0B4CC] mb-6">
                {data?.message || 'This membership ID could not be verified.'}
              </p>
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-6 text-center">
                  <p className="text-[#5C7599] text-sm">
                    The membership ID <span className="font-mono text-white">{membershipId}</span> is not registered in our system.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-[#A0B4CC] text-sm hover:text-[#00E5FF] transition-colors inline-flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to All Global Access
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
